import { SalesOrderInfo, fetchSalesOrders } from '../../../store/slices/salesOrders';
import classes from './DoctorDetailsTab.module.css';
import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import { IDoctor, fetchDoctors, selectDoctors } from '../../../store/slices/doctors';
import moment from 'moment';

import PrimaryButtonV2 from '../Buttons/PrimaryButtonV2';
import { toast } from 'react-toastify';
import { client } from '../../../networking/client';
import { selectBusinessPartnerId } from '../../../store/slices/auth';
import { DataTable } from '../../DataTable/DataTable';
import { isSameSecond, parseISO } from 'date-fns';
import Modal from '../Modals/Modal';
import { useTranslation } from '../../../hooks/useTranslations';

type Props = {
  selectedSalesOrder: SalesOrderInfo | undefined;
  isReports?: boolean;
};

export const DoctorDetailsTab: React.FC<Props> = ({ selectedSalesOrder, isReports }) => {
  const { translations } = useTranslation();
  const dispatch = useAppDispatch();
  const doctors: IDoctor[] = useAppSelector<IDoctor[]>(selectDoctors);
  const businessPartnerId = useAppSelector<string>(selectBusinessPartnerId);
  const [selectedDoctor, setSelectedDoctor] = useState<IDoctor>();
  const [newSelectedDoctor, setNewSelectedDoctor] = useState<any>();
  const [openModal, setOpenModal] = useState<boolean>(false);

  useEffect(() => {
    dispatch(fetchDoctors());
  }, [dispatch]);

  useEffect(() => {
    const tempoDoctorAssigned = doctors.find((doctor) =>
      doctor.person_appointments.find(
        (appointment) =>
          appointment.sales_order_id == selectedSalesOrder?.sales_order.sales_order_id &&
          appointment.sales_order_item_id ==
            selectedSalesOrder?.sales_order_item.sales_order_item_id
      )
    );
    if (tempoDoctorAssigned) {
      setSelectedDoctor(tempoDoctorAssigned);
    }
  }, [doctors, selectedSalesOrder]);

  const unavailableDoctors = useMemo(() => {
    return doctors
      .filter((doctor) =>
        doctor.person_appointments.find((appointment) => {
          return (
            appointment.start &&
            selectedSalesOrder?.sales_order_appointment.start &&
            isSameSecond(
              parseISO(appointment.start.toString()),
              parseISO(selectedSalesOrder?.sales_order_appointment.start.toString())
            )
          );
        })
      )
      .map((doctor) => doctor.person_id);
  }, [doctors, selectedSalesOrder]);

  const isConsultationWithin24Hours = useMemo(() => {
    if (!selectedSalesOrder?.sales_order_appointment.start) return true;
    const dateNow = new Date();
    const startDate = new Date(selectedSalesOrder.sales_order_appointment.start);
    let diff = (startDate.getTime() - dateNow.getTime()) / 1000;
    diff /= 60;
    return Math.round(diff) <= 1440;
  }, [selectedSalesOrder]);

  const assignDoctor = async (doctor: IDoctor) => {
    try {
      await client.put('/healthpoint/assign-consultation', {
        salesOrderId: selectedSalesOrder?.sales_order.sales_order_id,
        serviceId: selectedSalesOrder?.service.service_id,
        businessPartnerId: businessPartnerId,
        employeePersonId: selectedSalesOrder?.sales_order.business_person_id,
        doctorPersonId: doctor?.person_id,
        salesOrderItemId: selectedSalesOrder?.sales_order_item.sales_order_item_id,
      });
      toast.success(translations('doctor.assign.success'), {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      dispatch(fetchSalesOrders());
      dispatch(fetchDoctors());
    } catch (err) {
      toast.error(translations('error').toString(), {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    }
    setOpenModal(false);
  };
  const handleAssignDoctor = (value: IDoctor) => {
    if (selectedDoctor) {
      setOpenModal(true);
      setNewSelectedDoctor(value);
      return;
    }
    assignDoctor(value);
  };

  return (
    <div className={classes.container} style={isReports ? { height: '100%' } : {}}>
      {selectedDoctor ? (
        <div className={classes.selectedDoctorInfo}>
          <div className={classes.header}>{translations('doctor.assigned')}</div>
          <div className={classes.doctorTopicRow}>
            <div className={classes.columnItem}>
              <div className={classes.columnLabel}>{translations('doctor.id')}:</div>
              <div className={classes.columnValue}>{selectedDoctor?.person_id}</div>
            </div>
            <div className={classes.columnItem}>
              <div className={classes.columnLabel}>{translations('doctor.name')}:</div>
              <div className={classes.columnValue}>
                {`${selectedDoctor.person_firstname} ${selectedDoctor.person_lastname}`}
              </div>
            </div>
            <div className={classes.columnItem}>
              <div className={classes.columnLabel}>{translations('email.address')}:</div>
              <div className={classes.columnValue}>
                {selectedDoctor.business_partner_person_email}
              </div>
            </div>
          </div>
          <div className={classes.doctorTopicRow}>
            <div className={classes.columnItem}>
              <div className={classes.columnLabel}>{translations('gender')}:</div>
              <div className={classes.columnValue}>{selectedDoctor.gender}</div>
            </div>
            <div className={classes.columnItem}>
              <div className={classes.columnLabel}>{translations('birth.date')}:</div>
              <div className={classes.columnValue}>
                {selectedDoctor.person_birthdate
                  ? moment(selectedDoctor.person_birthdate).format('DD.MM.YYYY')
                  : ''}
              </div>
            </div>
          </div>
        </div>
      ) : isReports ? (
        <div className="emptyState">{translations('label.empty_state')}</div>
      ) : (
        <div className={classes.noAssigned}>{translations('no.doctor.assigned')}</div>
      )}

      {isReports ? (
        <></>
      ) : (
        <div className={classes.tableContainer}>
          <div className={classes.header} style={{ padding: '0px 10px' }}>
            {translations('doctors')}
          </div>
          <DataTable<IDoctor>
            rows={doctors}
            cellStyle={{
              padding: '10px',
              maxWidth: 120,
            }}
            columns={[
              {
                displayName: translations('doctor.id'),
                renderer: (value) => <div className={classes.idColumn}>{value.person_id}</div>,
              },
              {
                displayName: translations('doctor.name'),
                renderer: (value) => (
                  <div>{`${value.person_firstname} ${value.person_lastname}`}</div>
                ),
              },
              {
                displayName: translations('doctor.availability'),
                renderer: (value) => {
                  const isAvailable = !unavailableDoctors.includes(value.person_id);
                  return (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div className={isAvailable ? classes.greenDot : classes.redDot} />
                      {translations(isAvailable ? 'doctor.available' : 'doctor.not.available')}
                    </div>
                  );
                },
              },
              {
                displayName: '',
                renderer: (value) => {
                  return (
                    <PrimaryButtonV2
                      text={translations('doctor.assign')}
                      disabled={
                        !isConsultationWithin24Hours || selectedDoctor?.person_id == value.person_id
                      }
                      buttonStyle={{ height: 40, maxWidth: 110 }}
                      onClick={() => {
                        handleAssignDoctor(value);
                      }}
                    />
                  );
                },
              },
            ]}
          />
        </div>
      )}
      {openModal && (
        <Modal
          hideCloseButton
          isOpen={true}
          onClose={() => setOpenModal(!openModal)}
          maxWidth={'500px'}
          containerPadding={'100px 80px'}
        >
          <div className={classes.text}>{translations('doctor.are_you_dure')}</div>
          <div className={classes.buttonsContainer}>
            <div
              className={`${classes.button} ${classes.assingButton}`}
              onClick={() => assignDoctor(newSelectedDoctor)}
            >
              {translations('doctor.assign')}
            </div>
            <div
              className={`${classes.button} ${classes.cancelButton}`}
              onClick={() => setOpenModal(false)}
            >
              {translations('button.cancel')}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
